import {Inject, Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import en from '../../../../lang/generated/en.json';
import de from '../../../../lang/generated/de.json';
import it from '../../../../lang/generated/it.json';

import {SupportedLanguagesCodes, SupportedLocalesCodes} from '../../enums/enums';
import {environment} from '../../../../environments/environment';
import {NAVIGATOR} from '../../tokens/window';
import {THEME_LANG} from '../../constants/value';
import {SessionStorageService} from '../storage/session-storage/session-storage.service';
import {Language} from '../../interfaces/language';

@Injectable({
  providedIn: 'root',
})
export class LangService {
  isSingleLang = false;

  renderer: Renderer2;

  supportedLanguages: Language[] = [
    {code: SupportedLanguagesCodes.english, direction: 'ltr', label: 'English', shorthand: SupportedLocalesCodes.english},
    // {
    //   code: 'en-EN',
    //   direction: 'rtl',
    //   label: 'English - RTL',
    //   shorthand: 'enrtl',
    // },
    {code: SupportedLanguagesCodes.german, direction: 'ltr', label: 'Deutsch', shorthand: SupportedLocalesCodes.german},
    {code: SupportedLanguagesCodes.italian, direction: 'ltr', label: 'Italian', shorthand: SupportedLocalesCodes.italian},
  ];

  constructor(private translate: TranslateService,
              private rendererFactory: RendererFactory2,
              @Inject(NAVIGATOR) private readonly navigator: Navigator,
              private sessionStorageService: SessionStorageService) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  get language(): string {
    return this.translate.currentLang;
  }

  get languageShorthand(): string {
    return this.supportedLanguages.find((item) => item.code === this.translate.currentLang).shorthand;
  }

  get direction(): string {
    return this.supportedLanguages.find((item) => item.code === this.translate.currentLang).direction;
  }

  get languageLabel(): string {
    return this.supportedLanguages.find((item) => item.code === this.translate.currentLang).label;
  }

  set language(lang: string) {
    if(lang) {
      const isSupportedLanguage: boolean = this.supportedLanguages.some((item) => item.code.includes(lang));

      const language = isSupportedLanguage ? lang : this.getDefaultLanguage();

      if(isSupportedLanguage) {
        this.sessionStorageService.write(THEME_LANG, lang);
      }

      this.translate.use(language);
    } else {
      this.translate.use(this.getDefaultLanguage());
    }
  }

  init(): void {
    this.translate.setTranslation(SupportedLanguagesCodes.english, en);
    // this.translate.setTranslation('en-EN', en);
    this.translate.setTranslation(SupportedLanguagesCodes.german, de);
    this.translate.setTranslation(SupportedLanguagesCodes.italian, it);

    this.translate.setDefaultLang(
      environment.resolveOtherLanguagesToEnglish === 'true'
        ? SupportedLanguagesCodes.english
        : null
    );

    if (this.isSingleLang) {
      this.translate.use(this.getDefaultLanguage());
    } else {
      this.language = '';
    }
  }

  checkForDirectionChange(): void {
    this.renderer.removeClass(document.body, 'ltr');
    this.renderer.removeClass(document.body, 'rtl');
    this.renderer.addClass(document.body, this.direction);
    this.renderer.setAttribute(document.documentElement, 'direction', this.direction);
  }

  getTranslation(key: string, options?: Record<string, string>) {
    return this.instant(key, options);
  }

  instant(key: string, options = {}) {
    return this.translate.instant(key, options);
  }

  getDefaultLanguage() {
    return this.sessionStorageService.read(THEME_LANG) || this.resolveToSupportedLanguages(this.navigator.language);
  }

  resolveToSupportedLanguages(lang: string) {
    const mainLangSegment: string = lang.split('-')[0];
    return this.supportedLanguages
      .find(language => language.shorthand === mainLangSegment)?.code || SupportedLanguagesCodes.english;
  }

  setLanguage(languageCode: string): void {
    const language = this.supportedLanguages.find((supportedLanguage) => supportedLanguage.shorthand === languageCode);

    if (language) {
      this.language = language.code;
    }
  }
}
